import { TimesheetEstimatedTimeFragment } from 'generated/resources/graphql'
import moment from 'moment'

import { getBalanceAccruedMinutes } from 'modules/attendance/components/ClockIn/EmployeeTimeTrackingBreakdown/lib/getBalanceAccruedMinutes'
import type Store from 'modules/attendance/components/ClockIn/Store'
import { AggregatedWorkedTimeBlocks } from 'modules/attendance/lib/utils/AttendanceWorkedTimeUtils'
import EstimatedTimesUtils from 'modules/attendance/lib/utils/EstimatedTimes'
import { AggregateWorkedTimeBlocksFilter } from 'modules/attendance/store/models/AggregateWorkedTimeFilter'

export type WorkedTimeData = ReturnType<typeof calculateWorkedTime>

export const calculateWorkedTime = ({
  store,
  workedTime,
  estimatedTimes,
}: {
  store: Store
  workedTime?: AggregatedWorkedTimeBlocks | null
  estimatedTimes: TimesheetEstimatedTimeFragment[]
}) => {
  const today = moment()
  const estimatedTimesUntilToday = estimatedTimes.filter((item) => {
    const recordDate = moment(item.date)
    return recordDate.isSameOrBefore(today)
  })

  const {
    minutes = 0,
    workedTimeBlocks = [],
    trackedMinutes = 0,
    breaksMinutesRounded = 0,
  } = workedTime ?? {}
  const estimatedMinutes = estimatedTimesUntilToday
    ? EstimatedTimesUtils.sum(estimatedTimesUntilToday).minutes
    : 0

  const estimatedRegularMinutes = estimatedTimesUntilToday
    ? EstimatedTimesUtils.sum(estimatedTimesUntilToday).regularMinutes
    : 0

  const estimatedOvertimeMinutes = estimatedTimesUntilToday
    ? EstimatedTimesUtils.sum(estimatedTimesUntilToday).overtimeMinutes
    : 0

  const contractMinutes = estimatedTimesUntilToday
    ? EstimatedTimesUtils.sum(estimatedTimesUntilToday).contractMinutes
    : 0

  const contractMinutesEndOfPeriod = estimatedTimes
    ? EstimatedTimesUtils.sum(estimatedTimes).contractMinutes
    : 0

  const absencesMinutes = estimatedTimesUntilToday
    ? EstimatedTimesUtils.sum(estimatedTimesUntilToday).absencesMinutes
    : 0

  const estimatedMinutesForCurrentPeriod = store.estimatedMinutesForRange

  const filteredWorkedTimeBlocks = new AggregateWorkedTimeBlocksFilter(workedTimeBlocks)

  const regularMinutes = filteredWorkedTimeBlocks.regular.minutes
  const balanceAccruedMinutes = getBalanceAccruedMinutes(filteredWorkedTimeBlocks, estimatedMinutes)
  const balanceApprovedOvertime =
    filteredWorkedTimeBlocks.approved.overtime.withBalancePoolType.minutes
  const notBalanceApprovedOvertime =
    filteredWorkedTimeBlocks.approved.withTimeSettingsCategory.withoutBalancePoolType.overtime
      .minutes

  const dailyBalance = minutes - estimatedRegularMinutes

  const breaksBlocks = filteredWorkedTimeBlocks.reset.breaks.filteredBlocks

  const breaksMinutes =
    breaksBlocks.reduce((prev, current) => prev + Number(current.minutes), 0) || 0

  const accruedBalanceInRegularMinutes = balanceApprovedOvertime - balanceAccruedMinutes
  const totalRegularMinutesWithBalanceAccrued = regularMinutes + accruedBalanceInRegularMinutes

  const overtimeMinutes = notBalanceApprovedOvertime + balanceAccruedMinutes
  const unapprovedOvertimeMinutes = filteredWorkedTimeBlocks.reset.overtime.notApproved.minutes

  const discardedOrAddedMinutes = minutes + unapprovedOvertimeMinutes - trackedMinutes

  return {
    minutes,
    regularMinutes,
    contractMinutes,
    contractMinutesEndOfPeriod,
    absencesMinutes,
    breaksMinutes,
    breaksMinutesRounded,
    totalRegularMinutesWithBalanceAccrued,
    estimatedMinutes,
    estimatedRegularMinutes,
    estimatedOvertimeMinutes,
    estimatedMinutesForCurrentPeriod,
    trackedMinutes,
    overtimeMinutes,
    notBalanceApprovedOvertime,
    balanceApprovedOvertime,
    dailyBalance,
    unapprovedOvertimeMinutes,
    balanceAccruedMinutes,
    accruedBalanceInRegularMinutes,
    discardedOrAddedMinutes,
  }
}
